import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ArrowUpRight } from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const projects = [
  {
    label: 'Technoarreda | Interior Design',
    href: 'https://technoarreda-interior-design.vercel.app/',
    image: '/images/technoarreda.png',
    category: 'Interior Design',
    description: 'A modern interior design platform showcasing premium spaces with elegant visual storytelling.'
  },
  {
    label: 'Nymphai | Cosematics',
    href: 'https://nymphai-cosematics.vercel.app/',
    image: '/images/cosematics.png',
    category: 'Cosmetics',
    description: 'Luxury cosmetics brand site with immersive product showcases and seamless shopping experience.'
  },
  {
    label: 'Redsun | Business',
    href: 'https://redsun-business-website.vercel.app/',
    image: '/images/redsun.png',
    category: 'Business',
    description: 'Corporate business website built for impact — clean, professional, and conversion-focused.'
  },
  {
    label: 'Voxelo | E-Commerce Solution',
    href: 'https://voxelo-ecommerce-solution.vercel.app/',
    image: '/images/voxelo.png',
    category: 'E-Commerce',
    description: 'Full-featured e-commerce platform with streamlined checkout and dynamic product management.'
  },
  {
    label: 'Loud | Digital',
    href: 'https://loud-digital.vercel.app',
    image: '/images/loud.png',
    category: 'Digital',
    description: 'Digital agency site that amplifies brand presence through bold design and strategic content.'
  },
  {
    label: 'Music | history',
    href: 'https://music-history-three.vercel.app/',
    image: '/images/music.png',
    category: 'Music',
    description: 'An interactive journey through music history with rich media and timeline exploration.'
  },
  {
    label: 'Stokt | AI',
    href: 'https://stokt-cretive-design.vercel.app',
    image: '/images/stokt.png',
    category: 'AI',
    description: 'AI-driven creative design platform that merges machine learning with artistic expression.'
  },
  {
    label: 'Buck Sauce | Food',
    href: 'https://buck-sauce.vercel.app',
    image: '/images/buck.png',
    category: 'Food',
    description: 'Bold food brand site showcasing signature sauces with mouth-watering visuals and storytelling.'
  }
]

const categories = ['All', ...new Set(projects.map(p => p.category))]

export default function FeaturedProjects() {
  const [activeFilter, setActiveFilter] = useState('All')
  const sectionRef = useRef<HTMLDivElement>(null)

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.fp-title', { y: 60, opacity: 0 }, {
        y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none none',
        },
      })

      const cards = sectionRef.current?.querySelectorAll('.fp-card')
      if (cards && cards.length > 0) {
        gsap.fromTo(cards, { y: 80, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current, start: 'top 75%', toggleActions: 'play none none none', invalidateOnRefresh: true
          },
        })
      }

      if (cards) {
        cards.forEach((card) => {
          const handleMouseEnter = () => {
            gsap.to(card, { scale: 1.02, y: -6, duration: 0.4, ease: 'power2.out' })
            const img = card.querySelector('img')
            if (img) gsap.to(img, { scale: 1.08, duration: 0.6, ease: 'power2.out' })
          }
          const handleMouseLeave = () => {
            gsap.to(card, { scale: 1, y: 0, duration: 0.4, ease: 'power2.out' })
            const img = card.querySelector('img')
            if (img) gsap.to(img, { scale: 1, duration: 0.4, ease: 'power2.out' })
          }
          card.addEventListener('mouseenter', handleMouseEnter)
          card.addEventListener('mouseleave', handleMouseLeave)
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [activeFilter])

  return (
    <section ref={sectionRef} className="bg-[#050505] py-20 lg:py-40 px-6 lg:px-10 overflow-hidden">
      <h2 className="fp-title text-h2 text-white mb-6">
        Featured Projects
      </h2>

      <div className="flex flex-wrap gap-2 mb-10">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveFilter(cat)}
            className={`filter-pill ${activeFilter === cat ? 'active' : ''}`}
          >
            {cat.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filteredProjects.map((project, i) => (
          <a
            key={project.label}
            href={project.href}
            target="_blank"
            rel="noopener noreferrer"
            className="fp-card card-hover rounded-2xl overflow-hidden bg-[#0a0a0a] group block"
          >
            <div className="overflow-hidden relative">
              <img
                src={project.image}
                alt={project.label}
                className="w-full h-56 lg:h-64 object-cover transition-transform duration-700 shadow-[0_0_30px_rgba(183,0,14,0.15)]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-600/60 via-red-600/10 to-transparent to-60%" />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-mono text-[#d4540a]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>
              <h3 className="text-white font-medium text-base leading-tight mb-1">
                {project.label}
              </h3>
              <p className="text-sm text-[#666] mb-3 leading-relaxed line-clamp-2">
                {project.description}
              </p>
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <span className="text-xs text-[#9A9A9A]">{project.category}</span>
                <ArrowUpRight className="w-4 h-4 text-[#9A9A9A] opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-1 group-hover:translate-x-0" />
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}
